import React, { useEffect, useState } from "react";

import { Checkbox, DatePicker, Modal, TimePicker, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import i18next from "i18next";
import styled from "styled-components";

import { CutOffRule } from "./CalendarManagement";

const PickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const OrderInfoLabel = styled.p`
  color: #6d6d6d;
  font-size: 12px;
  line-height: 20px;
`;

type Props = {
  isOpen: boolean;
  selectedCutOffRule: CutOffRule;
  onSave: (cutOffRule: CutOffRule) => void;
  onClose: () => void;
};

const RoleModal: React.FC<Props> = ({
  isOpen,
  selectedCutOffRule,
  onSave,
  onClose,
}) => {
  const [currentDate, setCurrentDate] = useState<Dayjs>();
  const [isAnyDate, setIsAnyDate] = useState<boolean>(false);

  const [cutoffTime, setCutoffTime] = useState<Dayjs>();
  const [isWholeDay, setIsWholeDay] = useState<boolean>(false);

  const [lockedDeliveryDate, setLockedDeliveryDate] = useState<Dayjs>();

  useEffect(() => {
    resetStates();

    if (isOpen && selectedCutOffRule) {
      if (selectedCutOffRule.currentDate) {
        setCurrentDate(dayjs(selectedCutOffRule.currentDate.toString()));
      }

      setIsAnyDate(selectedCutOffRule.isAnyDate);

      if (selectedCutOffRule.isAnyDate) {
        setCurrentDate(null);
      }

      if (selectedCutOffRule.timeOfDay) {
        setCutoffTime(dayjs(selectedCutOffRule.timeOfDay.toString(), "HH:mm"));
      }

      setIsWholeDay(selectedCutOffRule.isWholeDay);

      if (selectedCutOffRule.isWholeDay) {
        setCutoffTime(null);
      }

      if (selectedCutOffRule.lockedDeliveryDate) {
        setLockedDeliveryDate(
          dayjs(selectedCutOffRule.lockedDeliveryDate?.toString())
        );
      }
    }
  }, [selectedCutOffRule, isOpen]);

  const isSaveButtonDisabled =
    (!currentDate && !isAnyDate) ||
    (!cutoffTime && !isWholeDay) ||
    !lockedDeliveryDate;

  const resetStates = () => {
    setCurrentDate(null);
    setIsAnyDate(false);
    setCutoffTime(null);
    setIsWholeDay(false);
    setLockedDeliveryDate(null);
  };

  const onOk = () => {
    const data = {
      currentDate: !isAnyDate ? currentDate?.format("YYYY-MM-DD") : null,
      id: selectedCutOffRule?.id,
      isAnyDate,
      isWholeDay,
      lockedDeliveryDate: lockedDeliveryDate?.format("YYYY-MM-DD"),
      timeOfDay: !isWholeDay ? cutoffTime?.format("HH:mm") : null,
    };
    onSave(data as CutOffRule);
  };

  const onCancel = () => {
    resetStates();
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      centered
      destroyOnHidden
      okText={i18next.t("save")}
      okButtonProps={{ disabled: isSaveButtonDisabled }}
      cancelButtonProps={{ style: { display: "none" } }}
      onOk={onOk}
      onCancel={onCancel}
      styles={{ body: { justifyContent: "center" } }}
    >
      <Typography.Title level={3} style={{ marginBottom: 40 }}>
        {i18next.t("new_rule")}
      </Typography.Title>

      <PickerWrapper>
        <OrderInfoLabel>{i18next.t("current_date")}</OrderInfoLabel>
        <DatePicker
          showNow={false}
          allowClear={false}
          value={currentDate}
          onChange={(date) => setCurrentDate(date)}
          minDate={dayjs()}
          format="DD/MM/YYYY"
          disabled={isAnyDate}
        />
        <Checkbox
          onChange={(e) => setIsAnyDate(e.target.checked)}
          checked={isAnyDate}
        >
          {i18next.t("any_date")}
        </Checkbox>
      </PickerWrapper>

      <PickerWrapper>
        <OrderInfoLabel>{i18next.t("cutoff_time")}</OrderInfoLabel>
        <TimePicker
          defaultOpenValue={cutoffTime}
          value={cutoffTime}
          onChange={(time) => setCutoffTime(time)}
          format="HH:mm"
          disabled={isWholeDay}
        />
        <Checkbox
          onChange={(e) => setIsWholeDay(e.target.checked)}
          checked={isWholeDay}
        >
          {i18next.t("whole_day")}
        </Checkbox>
      </PickerWrapper>

      <PickerWrapper>
        <OrderInfoLabel>{i18next.t("locked_delivery_date")}</OrderInfoLabel>
        <DatePicker
          showNow={false}
          allowClear={false}
          value={lockedDeliveryDate}
          onChange={(date) => setLockedDeliveryDate(date)}
          minDate={dayjs()}
          format="DD/MM/YYYY"
        />
      </PickerWrapper>
    </Modal>
  );
};
export default RoleModal;
